/* Gloomee Smart Free Shipping — cart drawer progress + recommendation controller.
   Vanilla custom element, no dependencies. Self-contained: does not read or
   modify any Shrine/main.js internals. Reacts to the theme's native AJAX cart
   re-renders passively (this element is simply re-created each time, since it
   lives inside the "cart-drawer" section markup that Shrine swaps in), and
   drives its own AJAX add-to-cart for each recommendation CTA using Shopify's
   public /cart/add.js + sections API — the same official mechanism Shrine
   itself uses for cart refreshes. */
(function () {
  if (customElements.get('gloomee-free-shipping')) return;

  var PREFERS_REDUCED_MOTION = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function storageGet(key) {
    try { return window.sessionStorage.getItem(key); } catch (e) { return null; }
  }
  function storageSet(key, value) {
    try { window.sessionStorage.setItem(key, value); } catch (e) { /* ignore (private mode, quota, etc.) */ }
  }
  function dispatchGloomeeEvent(name, detail) {
    document.dispatchEvent(new CustomEvent(name, { detail: detail || {}, bubbles: true }));
  }
  function nextFrame(fn) {
    window.requestAnimationFrame(function () { window.requestAnimationFrame(fn); });
  }

  class GloomeeFreeShippingElement extends HTMLElement {
    connectedCallback() {
      if (this._gloomeeInitialized) return;
      this._gloomeeInitialized = true;

      this.blockId = this.dataset.blockId || 'default';
      this.animationsEnabled = this.dataset.animationsEnabled === 'true' && !PREFERS_REDUCED_MOTION;
      this.successAnimationEnabled = this.dataset.successAnimationEnabled === 'true' && !PREFERS_REDUCED_MOTION;
      this.presentationMode = this.dataset.presentationMode || 'inline';
      this.popupDelay = parseInt(this.dataset.popupDelay, 10) || 0;
      this.dismissBehavior = this.dataset.dismissBehavior || 'until_cart_changes';
      this.cartAddUrl = this.dataset.cartAddUrl || '/cart/add.js';
      this.cartUpdateUrl = this.dataset.cartUpdateUrl || '/cart/update.js';

      this._initEntranceAnimation();
      this._initProgressBar();
      this._initRecommendationRow();
      this._initLiveRegion();
      this._initCheckoutUpsell();
    }

    disconnectedCallback() {
      if (this._popupTimer) window.clearTimeout(this._popupTimer);
    }

    /* ---------------- entrance ---------------- */

    _initEntranceAnimation() {
      if (!this.animationsEnabled) {
        this.classList.remove('gloomee-fs--enter');
        return;
      }
      nextFrame(function (self) {
        return function () { self.classList.remove('gloomee-fs--enter'); };
      }(this));
    }

    /* ---------------- progress bar ---------------- */

    _initProgressBar() {
      var fill = this.querySelector('[data-gloomee-fs-fill]');
      var targetProgress = parseFloat(this.dataset.progress) || 0;
      var unlocked = this.dataset.unlocked === 'true';

      var progressKey = 'gloomee-fs-progress-' + this.blockId;
      var unlockedKey = 'gloomee-fs-unlocked-' + this.blockId;
      var viewSignatureKey = 'gloomee-fs-view-sig-' + this.blockId;

      var previousProgressRaw = storageGet(progressKey);
      var previousProgress = previousProgressRaw === null ? targetProgress : parseFloat(previousProgressRaw);
      var previousUnlocked = storageGet(unlockedKey) === 'true';

      if (fill) {
        if (this.animationsEnabled && previousProgress !== targetProgress) {
          fill.style.transition = 'none';
          fill.style.width = previousProgress + '%';
          // Force reflow so the browser commits the "previous" width before we animate to the new one.
          void fill.offsetWidth;
          fill.style.transition = '';
          nextFrame(function () { fill.style.width = targetProgress + '%'; });
        } else {
          fill.style.width = targetProgress + '%';
        }
      }

      if (unlocked && !previousUnlocked) {
        dispatchGloomeeEvent('gloomee:free_shipping_unlocked', {
          subtotalCents: parseInt(this.dataset.subtotalCents, 10) || 0,
          thresholdCents: parseInt(this.dataset.thresholdCents, 10) || 0
        });
        if (this.successAnimationEnabled) {
          this.classList.add('gloomee-fs--sparkle');
          window.setTimeout(function (self) {
            return function () { self.classList.remove('gloomee-fs--sparkle'); };
          }(this), 800);
        }
      }

      storageSet(progressKey, String(targetProgress));
      storageSet(unlockedKey, String(unlocked));

      var viewSignature = Math.round(targetProgress) + ':' + unlocked;
      if (storageGet(viewSignatureKey) !== viewSignature) {
        storageSet(viewSignatureKey, viewSignature);
        dispatchGloomeeEvent('gloomee:shipping_progress_view', {
          progress: targetProgress,
          unlocked: unlocked,
          subtotalCents: parseInt(this.dataset.subtotalCents, 10) || 0,
          thresholdCents: parseInt(this.dataset.thresholdCents, 10) || 0
        });
      }
    }

    /* ---------------- live region (a11y) ---------------- */

    _initLiveRegion() {
      var live = this.querySelector('[data-gloomee-fs-live]');
      var message = this.querySelector('.gloomee-fs__message');
      if (!live || !message) return;
      var text = message.textContent.replace(/\s+/g, ' ').trim();
      // Set asynchronously: an aria-live region populated in the same paint as
      // its own insertion is not reliably announced by all screen readers.
      window.setTimeout(function () { live.textContent = text; }, 50);
    }

    /* ---------------- recommendation row (one or more cards) ---------------- */

    _initRecommendationRow() {
      var row = this.querySelector('[data-gloomee-fs-rec-row]');
      if (!row) return;

      var isPopup = row.classList.contains('gloomee-fs__rec-row--popup');
      var dismissKey = 'gloomee-fs-dismiss-' + this.blockId;
      var signature = this._rowSignature(row);

      var dismissed = false;
      var storedDismiss = storageGet(dismissKey);
      if (storedDismiss) {
        if (this.dismissBehavior === 'session') {
          dismissed = true;
        } else if (storedDismiss === signature) {
          dismissed = true;
        }
      }

      var cards = row.querySelectorAll('[data-gloomee-fs-rec]');
      cards.forEach(function (self) {
        return function (card) { self._bindRecCard(card); };
      }(this));

      var dismissButton = row.querySelector('[data-gloomee-fs-dismiss]');

      if (isPopup) {
        if (!dismissed) {
          this._popupTimer = window.setTimeout(function (self) {
            return function () {
              row.classList.add('gloomee-fs__rec-row--revealed');
              self._fireRecommendationViews(cards);
            };
          }(this), this.animationsEnabled ? this.popupDelay : Math.min(this.popupDelay, 150));
        }
      } else if (!dismissed) {
        this._fireRecommendationViews(cards);
      } else {
        row.style.display = 'none';
      }

      if (dismissButton) {
        dismissButton.addEventListener('click', function (self) {
          return function () {
            storageSet(dismissKey, signature);
            row.classList.remove('gloomee-fs__rec-row--revealed');
            if (self._popupTimer) window.clearTimeout(self._popupTimer);
          };
        }(this));
      }
    }

    _rowSignature(row) {
      var ids = [];
      row.querySelectorAll('[data-gloomee-fs-rec]').forEach(function (card) {
        ids.push(card.dataset.variantId + ':' + card.dataset.remainingCents);
      });
      return ids.join(',');
    }

    _fireRecommendationViews(cards) {
      var viewedKey = 'gloomee-fs-rec-view-sig-' + this.blockId;
      var signature = this._rowSignature({ querySelectorAll: function () { return cards; } });
      if (storageGet(viewedKey) === signature) return;
      storageSet(viewedKey, signature);
      cards.forEach(function (card) {
        dispatchGloomeeEvent('gloomee:shipping_recommendation_view', {
          variantId: card.dataset.variantId,
          productTitle: card.dataset.productTitle,
          priceCents: parseInt(card.dataset.priceCents, 10) || 0,
          remainingCents: parseInt(card.dataset.remainingCents, 10) || 0,
          clearsThreshold: card.dataset.clearsThreshold === 'true'
        });
      });
    }

    _bindRecCard(card) {
      if (card._gloomeeBound) return;
      card._gloomeeBound = true;
      var addButton = card.querySelector('[data-gloomee-fs-add]');
      if (addButton) {
        addButton.addEventListener('click', function (self) {
          return function () { self._handleAddRecommendation(card, addButton); };
        }(this));
      }
    }

    _isCardInCheckoutModal(card) {
      return !!card.closest('[data-gloomee-fs-checkout-modal]');
    }

    _handleAddRecommendation(card, button) {
      var self = this;
      if (button.dataset.state === 'loading') return;

      var variantId = card.dataset.variantId;
      var errorEl = card.querySelector('[data-gloomee-fs-rec-error]');
      if (errorEl) {
        errorEl.hidden = true;
        errorEl.textContent = '';
      }

      button.dataset.state = 'loading';
      button.disabled = true;

      var isSwap = card.dataset.swapEnabled === 'true' && card.dataset.swapTargetVariantId;
      var requestUrl, body;

      if (isSwap) {
        // Swap mode: remove the whole configured "swap out" line and add this variant, in one
        // request. /cart/update.js accepts a variant-id-keyed map — 0 removes a line entirely,
        // and a variant not yet in the cart is added at the given quantity.
        var updates = {};
        updates[card.dataset.swapTargetVariantId] = 0;
        updates[variantId] = 1;
        requestUrl = this.cartUpdateUrl;
        body = JSON.stringify({
          updates: updates,
          sections: 'cart-drawer,cart-icon-bubble',
          sections_url: window.location.pathname
        });
      } else {
        requestUrl = this.cartAddUrl;
        body = JSON.stringify({
          id: variantId,
          quantity: 1,
          sections: 'cart-drawer,cart-icon-bubble',
          sections_url: window.location.pathname
        });
      }

      fetch(requestUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: body
      })
        .then(function (response) {
          return response.json().then(function (json) {
            if (!response.ok) {
              var message = (json && (json.description || json.message)) || 'This item is currently unavailable.';
              throw new Error(message);
            }
            return json;
          });
        })
        .then(function (json) {
          dispatchGloomeeEvent('gloomee:shipping_recommendation_add', {
            variantId: variantId,
            productTitle: card.dataset.productTitle,
            priceCents: parseInt(card.dataset.priceCents, 10) || 0,
            swap: isSwap,
            swappedOutVariantId: isSwap ? card.dataset.swapTargetVariantId : null
          });
          button.dataset.state = 'success';
          card.classList.add('gloomee-fs__rec--success');
          var settleDelay = self.animationsEnabled ? 450 : 0;

          // A card inside the checkout-reminder modal needs different handling: the modal is
          // itself part of the same DOM subtree that a normal section-update replaces wholesale,
          // so refreshing while the modal is still open and holding it destroys the modal out
          // from under itself mid-transaction (the freeze/stuck-UI bug). Close it first — either
          // to send the shopper straight to checkout (auto-continue), or to fall back to a plain
          // drawer refresh underneath, but never leave it open across a section replace.
          if (self._isCardInCheckoutModal(card)) {
            window.setTimeout(function () {
              if (self.checkoutAutoContinue && self._checkoutContinue) {
                self._checkoutContinue();
              } else if (self._checkoutCloseModal) {
                // Wait for the modal's own close transition to fully finish before swapping the
                // drawer content in underneath — doing both at once cut the slide-down animation
                // off mid-flight, which read as a jarring snap rather than a clean close.
                self._checkoutCloseModal(function () {
                  self._applySectionUpdate(json);
                });
              } else {
                self._refreshCartAfterAdd(card, json);
              }
            }, settleDelay);
          } else {
            window.setTimeout(function () { self._refreshCartAfterAdd(card, json); }, settleDelay);
          }
        })
        .catch(function (error) {
          button.dataset.state = '';
          button.disabled = false;
          if (errorEl) {
            errorEl.hidden = false;
            errorEl.textContent = error.message || 'Something went wrong. Please try again.';
          }
        });
    }

    _refreshCartAfterAdd(card, response) {
      var self = this;
      var finish = function () { self._applySectionUpdate(response); };

      if (this.animationsEnabled && card.isConnected) {
        card.classList.add('gloomee-fs__rec--leaving');
        window.setTimeout(finish, 220);
      } else {
        finish();
      }
    }

    _applySectionUpdate(response) {
      if (!response || !response.sections) return;

      var cartDrawerHtml = response.sections['cart-drawer'];
      var cartIconHtml = response.sections['cart-icon-bubble'];

      if (cartDrawerHtml) {
        var drawerInner = document.querySelector('cart-drawer .drawer__inner, #CartDrawer .drawer__inner');
        var body = document.querySelector('.cart-drawer__body');
        var scrollTop = body ? body.scrollTop : 0;

        if (drawerInner) {
          var parsed = new DOMParser().parseFromString(cartDrawerHtml, 'text/html');
          var newInner = parsed.querySelector('.drawer__inner');
          if (newInner) {
            drawerInner.innerHTML = newInner.innerHTML;
            var newBody = document.querySelector('.cart-drawer__body');
            if (newBody) newBody.scrollTop = scrollTop;
          }
        }
      }

      if (cartIconHtml) {
        var iconBubble = document.getElementById('cart-icon-bubble');
        if (iconBubble) {
          var parsedIcon = new DOMParser().parseFromString(cartIconHtml, 'text/html');
          var newIconInner = parsedIcon.body.firstElementChild;
          iconBubble.innerHTML = newIconInner ? newIconInner.innerHTML : cartIconHtml;
        }
      }
    }

    /* ---------------- checkout reminder modal ---------------- */

    _initCheckoutUpsell() {
      if (this.dataset.checkoutUpsellEnabled !== 'true') return;
      var modal = this.parentElement ? this.parentElement.querySelector('[data-gloomee-fs-checkout-modal]') : null;
      if (!modal) return;

      this.checkoutAutoContinue = this.dataset.checkoutAutoContinue === 'true';

      var form = document.getElementById('CartDrawer-Form');
      if (!form || form._gloomeeCheckoutBound) return;
      form._gloomeeCheckoutBound = true;

      var self = this;
      var shownKey = 'gloomee-fs-checkout-shown-' + this.blockId;
      var bypass = false;
      var lastSubmitter = null;

      var closeModal = function (onClosed) {
        modal.classList.remove('gloomee-fs-modal--open');
        // Matches .gloomee-fs-modal__panel's own transform transition duration (0.35s) so callers
        // that need to wait for the close to visually finish (e.g. before swapping in fresh drawer
        // content) aren't racing the CSS animation.
        window.setTimeout(function () {
          modal.hidden = true;
          if (onClosed) onClosed();
        }, self.animationsEnabled ? 350 : 0);
      };

      var continueToCheckout = function () {
        closeModal();
        bypass = true;
        // The original button may have been replaced by an unrelated drawer refresh since the
        // modal opened — fall back to a fresh lookup rather than clicking a detached element.
        var submitter = (lastSubmitter && lastSubmitter.isConnected)
          ? lastSubmitter
          : document.getElementById('CartDrawer-Checkout');
        if (submitter) {
          submitter.click();
        } else {
          form.submit();
        }
      };

      // Exposed so a recommendation card's success handler (_handleAddRecommendation) can close
      // this modal, or send the shopper straight to checkout, without needing to know anything
      // about how the modal itself is implemented.
      this._checkoutCloseModal = closeModal;
      this._checkoutContinue = continueToCheckout;

      var openModal = function () {
        var body = modal.querySelector('[data-gloomee-fs-modal-body]');
        var progress = self.querySelector('.gloomee-fs__progress');
        var row = self.querySelector('[data-gloomee-fs-rec-row]');
        if (body && progress && row) {
          body.innerHTML = '';
          var progressClone = progress.cloneNode(true);
          var rowClone = row.cloneNode(true);
          rowClone.classList.remove('gloomee-fs__rec-row--popup', 'gloomee-fs__rec-row--revealed');
          rowClone.style.display = '';
          var rowDismiss = rowClone.querySelector('[data-gloomee-fs-dismiss]');
          if (rowDismiss) rowDismiss.remove();
          body.appendChild(progressClone);
          body.appendChild(rowClone);
          // The entrance animation for .gloomee-fs__rec is scoped to a ".gloomee-fs.gloomee-fs--motion"
          // ancestor for its "fade/slide in" keyframes. The modal body isn't inside that ancestor, so a
          // cloned card would otherwise be stuck at the animation's opacity:0 starting state forever —
          // present in the DOM, taking up layout space, but permanently invisible. Force it visible.
          rowClone.querySelectorAll('.gloomee-fs__rec').forEach(function (card) {
            card.style.animation = 'none';
            card.style.opacity = '1';
            card.style.transform = 'none';
          });
          rowClone.querySelectorAll('[data-gloomee-fs-rec]').forEach(function (card) {
            self._bindRecCard(card);
          });
        }
        modal.hidden = false;
        nextFrame(function () { modal.classList.add('gloomee-fs-modal--open'); });
        storageSet(shownKey, 'true');
      };

      form.addEventListener('submit', function (event) {
        if (bypass) { bypass = false; return; }

        var submitter = event.submitter;
        var isCheckoutSubmit = submitter && (submitter.name === 'checkout' || submitter.id === 'CartDrawer-Checkout');
        if (!isCheckoutSubmit) return;
        if (storageGet(shownKey) === 'true') return;

        event.preventDefault();
        lastSubmitter = submitter;
        openModal();
      });

      modal.querySelectorAll('[data-gloomee-fs-modal-close]').forEach(function (el) {
        el.addEventListener('click', closeModal);
      });

      var continueBtn = modal.querySelector('[data-gloomee-fs-modal-continue]');
      if (continueBtn) {
        continueBtn.addEventListener('click', continueToCheckout);
      }
    }
  }

  customElements.define('gloomee-free-shipping', GloomeeFreeShippingElement);
})();
